/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { CircleMinus, CirclePlus, Plus } from '@saleshandy/icons';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import Tag from '../../../../../shared/design-system/components/tag';
import {
  deepClone,
  findParentKey,
  getExceptFilterValues,
  getFilterKeys,
  getFilterValuesWithExtraValues,
  LOCAL_SEARCH_MAPPED_KEYS,
  MAPPED_KEYS,
  NOT_ALLOWED_APPEND_CHECKBOXES,
  V2_MAPPED_KEYS,
  LOCATION_V2_FILTER_KEYS,
  LOCATION_V2_SUPPORTED_TYPES,
  decodeLocationV2Entry,
  encodeLocationV2Entry,
  isLocationFilterKey,
  classifyLocationValue,
  getRegionChildrenKeys,
  withUniqueHierarchicalChildKeys,
  MULTILEVEL_INSTANCE_KEY_SEP,
  buildScopedChildKey,
  expandIndustryParentToChildren,
  getFundingDatePresetLabel,
} from '../../../helpers/helper';
import {
  DISABLED_BY_UNKNOWN_TOOLTIP,
  FilterComponentType,
  GetV2FilterSuggestionResponse,
  LocationV2SuggestionType,
} from '../../../types';
import {
  getFilterSuggestionAPI,
  getV2FilterSuggestionAPI,
  getCompanyNameDomainAPI,
} from '../../../helpers/leads.api';
import {
  findAllInstancesByValue,
  findInstancesByChildStorageKey,
  performLocalSearch,
  calculateCheckState,
  getCheckedKeys,
  SuggestionItem,
  getSuggestionValue,
  isCompanySuggestion,
  isCompanyNameFilterLimitReached,
  getCompanyNameFilterLimitTooltip,
  getCompanyNameFilterLimitInputError,
  isNumericCompanyFilterValue,
} from './v2-filter-checkbox.helpers';
import {
  RenderAutocomplete,
  RenderCheckboxGroup,
  getLabelTooltip,
} from './v2-filter-checkbox.components';
import {
  CheckboxItem,
  FilterCheckboxProps,
  HandleOnChangeParams,
} from './v2-filter-checkbox.types';

const INPUT_AS_FIRST_SUGGESTION_FILTER_KEYS = [
  'skills',
  'job_title',
  'location',
  'company_hq_location',
];

const FIRST_SUGGESTION_ON_ENTER_FILTER_KEYS = ['company_industry'];

const V2FilterCheckbox: React.FC<FilterCheckboxProps> = ({
  subFilter,
  handleQuery,
  isExpand = false,
  parentKey = '',
  hideIncludeExclude = false,
  bulkFilterSyncKey = {},
  isPhoneNumberNudgeOpen = false,
  searchLeads,
  pendingFilters,
  applyPendingFilters,
  isDisabledByUnknown = false,
  companyIdNameMap,
  setCompanyIdNameMap,
  onLockedClick,
  parentAppliedTagCount,
  isOnlyAppliedFilter = false,
  onClearLastAppliedFilter,
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const inputRef = useRef(null);
  const pendingFiltersRef = useRef<string>('');
  const pendingFiltersRefObj = useRef<URLSearchParams | null>(null);

  const {
    key: filterKey,
    list = [],
    inputClassName = '',
    inputPlaceHolder = '',
    inputLabel = '',
    hideSearchBox = false,
    removeTopMargin = false,
    infoCircleToolTipMapping = {},
    includeMaxLimit,
    expandedMaxFilterTags,
    advanceFilters,
    disableCustomInput = false,
  } = subFilter;

  const effectiveExpandedMaxFilterTags = expandedMaxFilterTags ?? 5;
  const hierarchicalList = useMemo(
    () => withUniqueHierarchicalChildKeys(list),
    [list],
  );

  const getParamsToRead = (): URLSearchParams => {
    if (pendingFilters != null) {
      const pendingString = pendingFilters.toString();
      pendingFiltersRef.current = pendingString;
      pendingFiltersRefObj.current = pendingFilters;
      return pendingFilters;
    }
    return queryParams;
  };

  const excludeKey = `exclude${filterKey
    .charAt(0)
    .toUpperCase()}${filterKey.slice(1)}`;

  const handleInitializeCheckboxes = (mainCheckbox: boolean) => {
    const paramsToRead = getParamsToRead();
    const legacyIncludedKeys = paramsToRead
      .getAll(filterKey)
      .map((item) => item?.replace(/"/g, ''));
    const legacyExcludedKeys = paramsToRead
      .getAll(excludeKey)
      .map((item) => item?.replace(/"/g, ''));

    // Also decode v2 params (location_v2 / excludeLocation_v2 etc.) and
    // translate them back into the checkbox tree: Region:<x> checks parent x
    // (and cascades to its children); Country:<y> checks that child; City /
    // State become custom rows so chips and tags stay in sync with the URL.
    const isLocationFilterLocal = isLocationFilterKey(filterKey);
    const v2LocationKeys = isLocationFilterLocal
      ? LOCATION_V2_FILTER_KEYS[
          filterKey as keyof typeof LOCATION_V2_FILTER_KEYS
        ]
      : null;
    const v2IncludeEntries = v2LocationKeys
      ? (paramsToRead
          .getAll(v2LocationKeys.v2Include)
          .map(decodeLocationV2Entry)
          .filter(Boolean) as { type: string; value: string }[])
      : [];
    const v2ExcludeEntries = v2LocationKeys
      ? (paramsToRead
          .getAll(v2LocationKeys.v2Exclude)
          .map(decodeLocationV2Entry)
          .filter(Boolean) as { type: string; value: string }[])
      : [];
    const v2IncludeValues = new Set(v2IncludeEntries.map((e) => e.value));
    const v2ExcludeValues = new Set(v2ExcludeEntries.map((e) => e.value));

    const includedKeys = Array.from(
      new Set(legacyIncludedKeys.concat(Array.from(v2IncludeValues))),
    );
    const excludedKeys = Array.from(
      new Set(legacyExcludedKeys.concat(Array.from(v2ExcludeValues))),
    );

    const baseCheckboxes = hierarchicalList.map((item) => {
      const parentIncluded = includedKeys.includes(item?.key);
      const parentExcluded = excludedKeys.includes(item?.key);

      let children = [];
      if (item?.children?.length > 0) {
        children = item.children.map((child) => {
          const childIncluded =
            includedKeys.includes(child?.key) ||
            includedKeys.includes(child?.value ?? '');
          const childExcluded =
            excludedKeys.includes(child?.key) ||
            excludedKeys.includes(child?.value ?? '');
          const checked =
            childIncluded || childExcluded || parentIncluded || parentExcluded;

          return {
            ...child,
            checked: checked ?? false,
            isExcluded:
              childExcluded || (parentExcluded && !childIncluded) || false,
          };
        });
      }

      const childrenCheckedCount = children?.filter((c) => c?.checked)?.length;
      const isIntermediate =
        childrenCheckedCount >= 1 &&
        item?.children?.length !== childrenCheckedCount;

      return {
        ...item,
        checked: isIntermediate
          ? 'indeterminate'
          : !!(parentIncluded || parentExcluded),
        isExcluded: parentExcluded || false,
        children,
      };
    });

    if (!NOT_ALLOWED_APPEND_CHECKBOXES?.includes(filterKey) && mainCheckbox) {
      const exceptListValues = getExceptFilterValues(
        includedKeys,
        hierarchicalList,
      );
      const includedCustomCheckboxes = exceptListValues.map((value) => ({
        key: value,
        value,
        checked: true,
        isExcluded: false,
        children: [],
      }));

      const exceptExcludeListValues = getExceptFilterValues(
        excludedKeys,
        hierarchicalList,
      );
      const excludedCustomCheckboxes = exceptExcludeListValues.map((value) => ({
        key: value,
        value,
        checked: true,
        isExcluded: true,
        children: [],
      }));

      return [
        ...baseCheckboxes,
        ...includedCustomCheckboxes,
        ...excludedCustomCheckboxes,
      ];
    }

    return baseCheckboxes;
  };

  // Use companyIdNameMap from props, fallback to empty object if not provided
  const effectiveCompanyIdNameMap = companyIdNameMap ?? {};

  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<string>('');
  const [checkboxes, setCheckboxes] = useState(() =>
    handleInitializeCheckboxes(true),
  );
  const [initialCheckboxes] = useState(() => handleInitializeCheckboxes(false));
  const [expanded, setExpanded] = useState([]);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [isV2ResponseType, setIsV2ResponseType] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const searchTermRef = useRef<string>('');
  const [exactMatch, setExactMatch] = useState<boolean>(() => {
    const paramsToRead = getParamsToRead();
    return paramsToRead.get(`${filterKey}_exactMatch`) === '1';
  });
  const [shouldSearchPastValues, setShouldSearchPastValues] = useState<boolean>(
    () => {
      const paramsToRead = getParamsToRead();
      return (
        paramsToRead.get(`${filterKey}_shouldSearchPast`) === '1' ||
        (filterKey === 'company_ids' &&
          paramsToRead.get('company_name_shouldSearchPast') === '1')
      );
    },
  );
  const [onFirstTimeKeyEnter, setOnFirstTimeKeyEnter] = useState<boolean>(
    false,
  );
  const [showTagTooltip, setShowTagTooltip] = useState<string | null>(null);
  const [showAllIncludeTags, setShowAllIncludeTags] = useState(false);
  const [showAllExcludeTags, setShowAllExcludeTags] = useState(false);

  const searchInputContainerId = `filter-${filterKey}-input`;
  const suggestionsContainerID = `autocomplete-${filterKey}`;

  const paramsToRead = getParamsToRead();

  const isLocationFilter = isLocationFilterKey(filterKey);
  const keepLocationSuggestionsOpenAfterPick = Boolean(applyPendingFilters);
  const shouldUseInputAsFirstSuggestion = INPUT_AS_FIRST_SUGGESTION_FILTER_KEYS.includes(
    filterKey,
  );
  const shouldPreventCustomInput =
    Boolean(disableCustomInput) ||
    FIRST_SUGGESTION_ON_ENTER_FILTER_KEYS.includes(filterKey);
  const shouldSelectFirstSuggestionOnEnter =
    shouldPreventCustomInput || shouldUseInputAsFirstSuggestion;
  const locationV2Keys = isLocationFilter
    ? LOCATION_V2_FILTER_KEYS[filterKey as keyof typeof LOCATION_V2_FILTER_KEYS]
    : null;
  const v2IncludeRaw = locationV2Keys
    ? paramsToRead.getAll(locationV2Keys.v2Include)
    : [];
  const v2ExcludeRaw = locationV2Keys
    ? paramsToRead.getAll(locationV2Keys.v2Exclude)
    : [];

  const isCompanyNameLimitReached = isCompanyNameFilterLimitReached(
    filterKey,
    paramsToRead,
  );
  const companyNameLimitTooltip = getCompanyNameFilterLimitTooltip();

  const isCompanyFilterValueAlreadyApplied = (value: string): boolean => {
    const normalized = String(value ?? '').trim();
    if (!normalized) return false;

    const params = getParamsToRead();
    const paramKeys = [
      'company_ids',
      'excludeCompany_ids',
      'company_name',
      'excludeCompany_name',
    ] as const;

    if (paramKeys.some((key) => params.getAll(key).includes(normalized))) {
      return true;
    }

    return checkboxes.some((c) => c.key === normalized && c.checked);
  };

  const blockCompanyNameNumericAtLimit = (value: string): boolean => {
    if (filterKey !== 'company_ids' || !isNumericCompanyFilterValue(value)) {
      return false;
    }
    if (!isCompanyNameFilterLimitReached(filterKey, getParamsToRead())) {
      return false;
    }
    if (isCompanyFilterValueAlreadyApplied(value)) {
      return false;
    }
    setInputError(getCompanyNameFilterLimitInputError());
    return true;
  };

  const getSuggestionsWithInputValue = (
    items: SuggestionItem[],
    rawInput: string,
  ): SuggestionItem[] => {
    const trimmedInput = String(rawInput || '').trim();
    const normalizedInput = trimmedInput.toLowerCase();
    const normalizedItems = Array.isArray(items) ? items : [];

    if (!shouldUseInputAsFirstSuggestion || !trimmedInput) {
      return normalizedItems;
    }

    const exactMatchIndex = normalizedItems.findIndex((item) => {
      const normalizedSuggestion = getSuggestionValue(item)
        .trim()
        .toLowerCase();

      return normalizedSuggestion === normalizedInput;
    });

    if (exactMatchIndex === -1) {
      return [trimmedInput, ...normalizedItems];
    }

    const exactMatchItem = normalizedItems[exactMatchIndex];
    return [
      exactMatchItem,
      ...normalizedItems.filter((_, index) => index !== exactMatchIndex),
    ];
  };

  const isIncludeDisabled =
    includeMaxLimit &&
    filterKey !== 'company_ids' &&
    paramsToRead.getAll(filterKey)?.length + v2IncludeRaw.length >=
      includeMaxLimit;
  const isDropdownIncludeDisabled =
    isCompanyNameLimitReached || isIncludeDisabled;
  const isDropdownExcludeDisabled = isCompanyNameLimitReached;
  const shouldShowIncludeMaxLimitTooltip =
    isIncludeDisabled &&
    (filterKey === 'competitors_company_name' ||
      filterKey === 'website_domain');
  const includeTooltip = shouldShowIncludeMaxLimitTooltip
    ? `You can add up to ${includeMaxLimit} companies at a time for more relevant results. Remove one to add another.`
    : 'Include';
  const dropdownIncludeTooltip = isCompanyNameLimitReached
    ? companyNameLimitTooltip
    : includeTooltip;
  const dropdownExcludeTooltip = isCompanyNameLimitReached
    ? companyNameLimitTooltip
    : 'Exclude';

  const companyNameIncludeValues =
    filterKey === 'company_ids'
      ? paramsToRead
          .getAll('company_name')
          .map((item) => item?.replace(/"/g, ''))
          .filter(Boolean)
      : [];
  const companyNameExcludeValues =
    filterKey === 'company_ids'
      ? paramsToRead
          .getAll('excludeCompany_name')
          .map((item) => item?.replace(/"/g, ''))
          .filter(Boolean)
      : [];

  const handlePreQuery = (
    updatedCheckboxes,
    showExactMatch?: boolean,
    showIncludePastJobs?: boolean,
    isSearchLeads?: boolean,
    onKeyEnter = false,
    competitorCompanyIdParam?: number,
  ) => {
    const includedKeys = getCheckedKeys(
      updatedCheckboxes,
      true,
      showExactMatch,
    );
    const excludedKeys = getCheckedKeys(
      updatedCheckboxes,
      false,
      showExactMatch,
    );

    const finalIncludedKeys =
      filterKey === 'company_ids'
        ? [
            ...includedKeys,
            ...companyNameIncludeValues.filter(
              (v) => !excludedKeys.includes(v),
            ),
          ]
        : includedKeys;
    const finalExcludedKeys =
      filterKey === 'company_ids'
        ? [
            ...excludedKeys,
            ...companyNameExcludeValues.filter(
              (v) => !includedKeys.includes(v),
            ),
          ]
        : excludedKeys;

    handleQuery({
      filterKey,
      filterExcludeKey: excludeKey,
      filterValue: {
        includedKeys: finalIncludedKeys,
        excludedKeys: finalExcludedKeys,
      },
      filterType: FilterComponentType?.MULTILEVEL_CHECKBOX,
      showExactMatch,
      showIncludePastJobs,
      isSearchLeads,
      onKeyEnter,
      competitorCompanyId: competitorCompanyIdParam,
    });
  };

  const applyLocationV2Query = (
    nextIncludes: string[],
    nextExcludes: string[],
    showExactMatch?: boolean,
  ) => {
    if (!locationV2Keys) return;
    handleQuery({
      filterKey: locationV2Keys.v2Include,
      filterExcludeKey: locationV2Keys.v2Exclude,
      filterValue: {
        includedKeys: nextIncludes,
        excludedKeys: nextExcludes,
      },
      filterType: FilterComponentType.MULTILEVEL_CHECKBOX,
      onKeyEnter: false,
      ...(showExactMatch !== undefined && { showExactMatch }),
    });
  };

  const handleV2LocationPick = (
    suggestion: SuggestionItem,
    isExclude: boolean,
  ): boolean => {
    if (!locationV2Keys) return false;
    const suggestionType = (suggestion as any)?.type;
    if (!LOCATION_V2_SUPPORTED_TYPES.includes(suggestionType)) return false;
    const suggestionValue = getSuggestionValue(suggestion);
    if (!suggestionValue) return false;
    const encoded = encodeLocationV2Entry(
      suggestionType as LocationV2SuggestionType,
      suggestionValue,
    );
    const currentIncludes = v2IncludeRaw.slice();
    const currentExcludes = v2ExcludeRaw.slice();
    const existsInTarget = isExclude
      ? currentExcludes.includes(encoded)
      : currentIncludes.includes(encoded);
    if (existsInTarget) {
      if (!keepLocationSuggestionsOpenAfterPick) {
        setSuggestions([]);
        setShowAutoComplete(false);
        setInputValue('');
        searchTermRef.current = '';
      }
      setSelectedSuggestionIndex(-1);
      return true;
    }
    // Ensure a value lives in only one bucket (include or exclude).
    const nextIncludes = isExclude
      ? currentIncludes.filter((v) => v !== encoded)
      : [...currentIncludes, encoded];
    const nextExcludes = isExclude
      ? [...currentExcludes.filter((v) => v !== encoded), encoded]
      : currentExcludes.filter((v) => v !== encoded);
    applyLocationV2Query(nextIncludes, nextExcludes);
    if (!keepLocationSuggestionsOpenAfterPick) {
      setSuggestions([]);
      setShowAutoComplete(false);
      setInputValue('');
      searchTermRef.current = '';
    }
    setSelectedSuggestionIndex(-1);
    setOnFirstTimeKeyEnter(true);
    return true;
  };

  const handleV2LocationRemove = (encoded: string, isExclude: boolean) => {
    if (!locationV2Keys) return;
    const decoded = decodeLocationV2Entry(encoded);
    // Removing a Region tag should also drop Country:<child> entries for that region
    // to keep the checkbox tree and v2 URL state in sync.
    const encodedToRemove = new Set<string>([encoded]);
    if (decoded?.type === 'Region') {
      const childKeys = getRegionChildrenKeys(decoded.value, hierarchicalList);
      childKeys.forEach((childKey) => {
        encodedToRemove.add(encodeLocationV2Entry('Country', childKey));
      });
    }
    const nextIncludes = isExclude
      ? v2IncludeRaw.filter((v) => !encodedToRemove.has(v) || v !== encoded)
      : v2IncludeRaw.filter((v) => !encodedToRemove.has(v));
    const nextExcludes = isExclude
      ? v2ExcludeRaw.filter((v) => !encodedToRemove.has(v))
      : v2ExcludeRaw.filter((v) => !encodedToRemove.has(v) || v !== encoded);

    const allFiltersRemoved =
      nextIncludes.length === 0 &&
      nextExcludes.length === 0 &&
      paramsToRead.getAll(filterKey).filter(Boolean).length === 0 &&
      paramsToRead.getAll(excludeKey).filter(Boolean).length === 0;

    if (allFiltersRemoved) {
      setExactMatch(false);
      applyLocationV2Query(nextIncludes, nextExcludes, false);
    } else {
      applyLocationV2Query(nextIncludes, nextExcludes);
    }
  };

  const handleV2LocationReset = (
    suggestion: SuggestionItem,
    isExclude: boolean,
  ): boolean => {
    if (!locationV2Keys) return false;
    const suggestionType = (suggestion as any)?.type;
    if (!LOCATION_V2_SUPPORTED_TYPES.includes(suggestionType)) return false;
    const suggestionValue = getSuggestionValue(suggestion);
    if (!suggestionValue) return false;

    const encoded = encodeLocationV2Entry(
      suggestionType as LocationV2SuggestionType,
      suggestionValue,
    );
    const targetValues = isExclude ? v2ExcludeRaw : v2IncludeRaw;
    if (!targetValues.includes(encoded)) return false;

    handleV2LocationRemove(encoded, isExclude);
    return true;
  };

  const getV2LocationTagLabel = (encoded: string): string | null => {
    const decoded = decodeLocationV2Entry(encoded);
    if (!decoded) return null;
    if (
      decoded.type === 'Country' &&
      decoded.value.includes(MULTILEVEL_INSTANCE_KEY_SEP)
    ) {
      const sepIdx = decoded.value.indexOf(MULTILEVEL_INSTANCE_KEY_SEP);
      return decoded.value.slice(sepIdx + MULTILEVEL_INSTANCE_KEY_SEP.length);
    }
    return decoded.value;
  };

  // Split checkbox state into v2 (Region:/Country:) and legacy (custom text)
  // buckets for Location / Company HQ Location filters.
  const buildLocationQueries = (items: any[]) => {
    const v2Includes: string[] = [];
    const v2Excludes: string[] = [];
    const legacyIncludes: string[] = [];
    const legacyExcludes: string[] = [];
    const knownParentKeys = new Set(hierarchicalList.map((l: any) => l?.key));

    items.forEach((item) => {
      if (!item?.checked) return;

      const isKnownParent = knownParentKeys.has(item.key);
      if (isKnownParent && item.children?.length > 0) {
        if (item.checked === true) {
          const bucket = item.isExcluded ? v2Excludes : v2Includes;
          bucket.push(encodeLocationV2Entry('Region', item.key));
        } else if (item.checked === 'indeterminate') {
          item.children.forEach((child: any) => {
            if (!child?.checked) return;
            const bucket = child.isExcluded ? v2Excludes : v2Includes;
            const leaf =
              child.value ||
              (typeof child.key === 'string' &&
              child.key.includes(MULTILEVEL_INSTANCE_KEY_SEP)
                ? child.key.split(MULTILEVEL_INSTANCE_KEY_SEP)[1]
                : child.key);
            const countryV2 = buildScopedChildKey(item.key, leaf);
            bucket.push(encodeLocationV2Entry('Country', countryV2));
          });
        }
        return;
      }

      // Unknown / custom row. Promote to v2 if it matches LOCATION_DATA;
      // otherwise keep it in the legacy bucket.
      const classified = classifyLocationValue(item.key, hierarchicalList);
      if (classified) {
        const bucket = item.isExcluded ? v2Excludes : v2Includes;
        bucket.push(
          encodeLocationV2Entry(classified.type, classified.matchedKey),
        );
      } else {
        const sourceV2Raw = item.isExcluded ? v2ExcludeRaw : v2IncludeRaw;
        const v2Match = sourceV2Raw.find((raw) => {
          const decoded = decodeLocationV2Entry(raw);
          return decoded?.value === item.key;
        });
        if (v2Match) {
          const bucket = item.isExcluded ? v2Excludes : v2Includes;
          bucket.push(v2Match);
        } else {
          const bucket = item.isExcluded ? legacyExcludes : legacyIncludes;
          bucket.push(item.key);
        }
      }
    });

    // Dedup (a country under multiple regions or repeated via custom text).
    return {
      v2Includes: Array.from(new Set(v2Includes)),
      v2Excludes: Array.from(new Set(v2Excludes)),
      legacyIncludes: Array.from(new Set(legacyIncludes)),
      legacyExcludes: Array.from(new Set(legacyExcludes)),
    };
  };

  // Promote a typed value to the v2 bucket if it matches LOCATION_DATA.
  // Returns true when the value was handled, false when caller should
  // fall through to the legacy free-text path.
  const handleTypedLocationValue = (
    rawValue: string,
    isExclude: boolean,
  ): boolean => {
    if (!locationV2Keys) return false;
    const trimmed = rawValue?.trim();
    if (!trimmed) return false;
    const classified = classifyLocationValue(trimmed, hierarchicalList);
    if (!classified) return false;
    const encoded = encodeLocationV2Entry(
      classified.type,
      classified.matchedKey,
    );
    const currentIncludes = v2IncludeRaw.slice();
    const currentExcludes = v2ExcludeRaw.slice();
    const existsInTarget = isExclude
      ? currentExcludes.includes(encoded)
      : currentIncludes.includes(encoded);
    if (existsInTarget) {
      if (!keepLocationSuggestionsOpenAfterPick) {
        setSuggestions([]);
        setShowAutoComplete(false);
        setInputValue('');
        searchTermRef.current = '';
      }
      setSelectedSuggestionIndex(-1);
      return true;
    }
    const nextIncludes = isExclude
      ? currentIncludes.filter((v) => v !== encoded)
      : Array.from(new Set([...currentIncludes, encoded]));
    const nextExcludes = isExclude
      ? Array.from(
          new Set([...currentExcludes.filter((v) => v !== encoded), encoded]),
        )
      : currentExcludes.filter((v) => v !== encoded);
    applyLocationV2Query(nextIncludes, nextExcludes);
    if (!keepLocationSuggestionsOpenAfterPick) {
      setSuggestions([]);
      setShowAutoComplete(false);
      setInputValue('');
      searchTermRef.current = '';
    }
    setSelectedSuggestionIndex(-1);
    setOnFirstTimeKeyEnter(true);
    return true;
  };

  const dispatchLocationQuery = (
    updatedCheckboxes: any[],
    nextExactMatch: boolean | undefined,
    isSearchLeadsArg: boolean | undefined,
    onKeyEnterArg: boolean,
  ) => {
    if (!locationV2Keys) return;
    const {
      v2Includes,
      v2Excludes,
      legacyIncludes,
      legacyExcludes,
    } = buildLocationQueries(updatedCheckboxes);
    const hasV2 = v2Includes.length > 0 || v2Excludes.length > 0;
    const hasLegacy = legacyIncludes.length > 0 || legacyExcludes.length > 0;
    const exactMatchDefined = nextExactMatch !== undefined;

    handleQuery({
      filterKey: locationV2Keys.v2Include,
      filterExcludeKey: locationV2Keys.v2Exclude,
      filterValue: { includedKeys: v2Includes, excludedKeys: v2Excludes },
      filterType: FilterComponentType.MULTILEVEL_CHECKBOX,
      showExactMatch: exactMatchDefined
        ? hasV2
          ? nextExactMatch
          : false
        : undefined,
      isSearchLeads: isSearchLeadsArg,
      onKeyEnter: false,
      onFiltersUpdated: (updated) => {
        handleQuery({
          filterKey,
          filterExcludeKey: excludeKey,
          filterValue: {
            includedKeys: legacyIncludes,
            excludedKeys: legacyExcludes,
          },
          filterType: FilterComponentType.MULTILEVEL_CHECKBOX,
          showExactMatch: exactMatchDefined
            ? hasLegacy
              ? nextExactMatch
              : false
            : undefined,
          isSearchLeads: isSearchLeadsArg,
          onKeyEnter: onKeyEnterArg,
          baseFilters: updated,
        });
      },
    });
  };

  useEffect(() => {
    if (bulkFilterSyncKey?.[filterKey]) {
      setCheckboxes(handleInitializeCheckboxes(true));
    }
  }, [bulkFilterSyncKey, filterKey]);

  useEffect(() => {
    setCheckboxes(handleInitializeCheckboxes(true));
    if (pendingFilters != null) {
      const pendingString = pendingFilters.toString();
      pendingFiltersRef.current = pendingString;
      pendingFiltersRefObj.current = pendingFilters;
    } else {
      pendingFiltersRef.current = '';
      pendingFiltersRefObj.current = null;
    }
  }, [location?.search, filterKey, pendingFilters]);

  const handleOnChange = ({
    action = 'changeParent',
    groupName = null,
    childName = null,
    exclude = null,
    isSearchLeads = false,
    onKeyEnter = false,
    competitorCompanyId,
    skipImmediateApplyOnRemove = false,
  }: HandleOnChangeParams) => {
    const shouldApplyImmediatelyOnRemove =
      action === 'removeTag' &&
      !!applyPendingFilters &&
      !skipImmediateApplyOnRemove;
    const effectiveOnKeyEnter = onKeyEnter || shouldApplyImmediatelyOnRemove;
    const effectiveIsSearchLeads =
      isSearchLeads || shouldApplyImmediatelyOnRemove;

    if (!groupName && isSearchLeads && onKeyEnter) {
      searchLeads(null, isSearchLeads);
      return;
    }

    if (
      groupName &&
      (action === 'inputOption' || action === 'excludeOption') &&
      blockCompanyNameNumericAtLimit(String(groupName))
    ) {
      return;
    }

    let groupIndex = -1;
    let childIndex = -1;
    if (groupName) {
      groupIndex = checkboxes?.findIndex((c) => c?.key === groupName);
    }
    if (
      childName &&
      groupIndex > -1 &&
      checkboxes[groupIndex] &&
      checkboxes[groupIndex]?.children?.length > 0
    ) {
      childIndex = checkboxes[groupIndex]?.children?.findIndex(
        (c) => c?.key === childName,
      );
    }

    setCheckboxes((prevState) => {
      let updatedValues = deepClone(prevState);
      const currentParams = getParamsToRead();

      // Skip location preservation when removing a tag - we don't want to re-add it
      if (
        (filterKey === 'location' || filterKey === 'excludeLocation') &&
        action !== 'removeTag'
      ) {
        const existingIndividualTagsValues =
          currentParams
            .getAll(filterKey)
            ?.map((item) => item.replace(/"/g, '')) || [];

        let tagValues = Array.from(new Set([...existingIndividualTagsValues]));

        const filteredObjects = updatedValues.filter((obj) => obj.checked);

        const keysToRemove = new Set();
        filteredObjects.forEach((obj) => {
          keysToRemove.add(obj.key); // Add the main key
          if (obj.children) {
            obj.children.forEach((child) => keysToRemove.add(child.key));
          }
        });

        tagValues = tagValues.filter((item) => !keysToRemove.has(item));

        tagValues.forEach((tag) => {
          const isTagExcluded = currentParams
            .getAll(excludeKey)
            .map((item) => item.replace(/"/g, ''))
            .includes(tag);
          updatedValues.push({
            key: tag,
            value: tag,
            checked: true,
            isExcluded: isTagExcluded,
            children: [],
          });
        });
      }

      const updateItemsWithInstanceKeys = (
        items: any[],
        instanceKeys: Set<string>,
        isExclude: boolean,
      ) => {
        let checkedCount = 0;
        const updated = items.map((item) => {
          const updatedItem = deepClone(item);

          if (instanceKeys.has(item.key)) {
            updatedItem.checked = true;
            checkedCount = 1;
            updatedItem.isExcluded = isExclude;

            if (updatedItem.children?.length > 0) {
              updatedItem.children = updatedItem.children.map((child) => ({
                ...child,
                checked: true,
                isExcluded: isExclude,
              }));
            }
          } else if (item.children?.length > 0) {
            let hasMatchingChild = false;
            updatedItem.children = updatedItem.children.map((child) => {
              if (instanceKeys.has(child.key)) {
                hasMatchingChild = true;
                checkedCount = 1;
                return { ...child, checked: true, isExcluded: isExclude };
              }
              return child;
            });

            if (hasMatchingChild) {
              const checkedChildren = updatedItem.children.filter(
                (c) => c.checked,
              ).length;
              const excludedChildren = updatedItem.children.filter(
                (c) => c.checked && c.isExcluded,
              ).length;
              const includedChildren = updatedItem.children.filter(
                (c) => c.checked && !c.isExcluded,
              ).length;
              const hasMixedActions =
                includedChildren > 0 && excludedChildren > 0;

              if (hasMixedActions) {
                updatedItem.checked = 'indeterminate';
                updatedItem.isExcluded = false;
              } else {
                updatedItem.checked = calculateCheckState({
                  checkedChild: checkedChildren,
                  totalChild: updatedItem.children.length,
                });
                updatedItem.isExcluded =
                  checkedChildren > 0 && excludedChildren === checkedChildren;
              }
            }
          }

          return updatedItem;
        });
        return { updated, checkedCount };
      };

      const includeExcludeOptions = (isExclude: boolean) => {
        let checkedCount = 0;
        const allInstances = findAllInstancesByValue(
          groupName,
          hierarchicalList,
        );

        if (allInstances.length > 0) {
          const instanceKeys = new Set<string>();
          allInstances.forEach((instance) => {
            instanceKeys.add(instance.childKey);
            if (instance.parentKey === instance.childKey) {
              instanceKeys.add(instance.parentKey);
            }
          });

          const result = updateItemsWithInstanceKeys(
            updatedValues,
            instanceKeys,
            isExclude,
          );
          updatedValues = result.updated;
          checkedCount = result.checkedCount;
        } else {
          const tagInputKey =
            getFilterKeys([groupName], hierarchicalList)[0] || groupName;
          const newGroupIndex = tagInputKey
            ? checkboxes?.findIndex((c) => c?.key === tagInputKey)
            : -1;

          updatedValues = updatedValues.map((item) => {
            const updatedItem = deepClone(item);

            if (newGroupIndex > -1 && item?.key === tagInputKey) {
              updatedItem.checked = true;
              checkedCount = 1;
              updatedItem.isExcluded = isExclude;
            } else {
              const foundParentKey = findParentKey(
                hierarchicalList,
                tagInputKey,
              );
              if (foundParentKey) {
                const foundGroupIndex = checkboxes?.findIndex(
                  (c) => c?.key === foundParentKey,
                );
                if (
                  tagInputKey &&
                  foundGroupIndex > -1 &&
                  checkboxes[foundGroupIndex]?.children?.length > 0
                ) {
                  const foundChildIndex = checkboxes[
                    foundGroupIndex
                  ].children.findIndex((c) => c?.key === tagInputKey);
                  if (foundGroupIndex > -1 && item?.key === foundParentKey) {
                    updatedItem.children = updatedItem.children?.map(
                      (child, index) => {
                        const childItem = deepClone(child);
                        if (index === foundChildIndex) {
                          childItem.checked = true;
                          checkedCount = 1;
                          childItem.isExcluded = isExclude;
                        }
                        return childItem;
                      },
                    );

                    const checkedChildren = updatedItem.children.filter(
                      (c) => c.checked,
                    ).length;
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });

                    const excludedChildren = updatedItem.children.filter(
                      (c) => c.isExcluded,
                    ).length;
                    updatedItem.isExcluded = excludedChildren > 0;
                  }
                }
              }
            }
            return updatedItem;
          });

          if (
            !NOT_ALLOWED_APPEND_CHECKBOXES?.includes(filterKey) &&
            checkedCount === 0
          ) {
            updatedValues.push({
              key: tagInputKey,
              value: tagInputKey,
              checked: true,
              isExcluded: isExclude,
              children: [],
            });
          }
        }
      };

      switch (action) {
        case 'changeParent':
          if (groupIndex > -1) {
            const allSelected = prevState[groupIndex]?.checked === true;
            const newChecked = !allSelected;
            updatedValues[groupIndex] = {
              ...updatedValues[groupIndex],
              checked: newChecked,
              children: prevState[groupIndex]?.children?.map((child) => ({
                ...child,
                checked: newChecked,
                isExcluded: false,
              })),
            };
          }
          break;
        case 'changeChild':
          if (groupIndex > -1 && childIndex > -1) {
            const clickedChild = prevState[groupIndex]?.children[childIndex];
            const newCheckedState = !clickedChild?.checked;

            const instanceKeys = new Set<string>([clickedChild.key]);

            updatedValues = updatedValues.map((item) => {
              const updatedItem = deepClone(item);

              if (item.children?.length > 0) {
                const hasMatchingChild = item.children.some((child) =>
                  instanceKeys.has(child.key),
                );

                if (hasMatchingChild) {
                  const parentIsFullyIncluded =
                    item.checked === true && item.isExcluded === false;
                  const parentIsFullyExcluded =
                    item.checked === true && item.isExcluded === true;

                  updatedItem.children = updatedItem.children.map((child) => {
                    if (instanceKeys.has(child.key)) {
                      return {
                        ...child,
                        checked: newCheckedState,
                        isExcluded: newCheckedState
                          ? item.isExcluded || false
                          : false,
                      };
                    }
                    // If unchecking and parent was fully included/excluded,
                    // siblings should remain in parent's action (explicitly set)
                    if (
                      !newCheckedState &&
                      (parentIsFullyIncluded || parentIsFullyExcluded)
                    ) {
                      return {
                        ...child,
                        isExcluded: parentIsFullyExcluded,
                      };
                    }
                    return child;
                  });

                  const checkedChildren = updatedItem.children.filter(
                    (c) => c.checked,
                  ).length;
                  const excludedChildren = updatedItem.children.filter(
                    (c) => c.isExcluded,
                  ).length;
                  const includedChildren = updatedItem.children.filter(
                    (c) => c.checked && !c.isExcluded,
                  ).length;

                  // Parent should be indeterminate if children have mixed actions
                  const hasMixedActions =
                    includedChildren > 0 && excludedChildren > 0;

                  if (hasMixedActions) {
                    updatedItem.checked = 'indeterminate';
                    updatedItem.isExcluded = false;
                  } else {
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });
                    // Parent is excluded only if ALL checked children are excluded
                    updatedItem.isExcluded =
                      checkedChildren > 0 &&
                      excludedChildren === checkedChildren;
                  }
                }
              } else if (instanceKeys.has(item.key)) {
                updatedItem.checked = newCheckedState;
                updatedItem.isExcluded = newCheckedState
                  ? updatedItem.isExcluded
                  : false;
              }

              return updatedItem;
            });
          }
          break;
        case 'includeParent':
          if (groupIndex > -1) {
            updatedValues[groupIndex] = {
              ...updatedValues[groupIndex],
              checked: true,
              isExcluded: false,
              children: prevState[groupIndex]?.children?.map((child) => ({
                ...child,
                checked: true,
                isExcluded: false,
              })),
            };
          }
          break;
        case 'includeChild':
          if (groupIndex > -1 && childIndex > -1) {
            const clickedChild = prevState[groupIndex]?.children[childIndex];

            const instanceKeys = new Set<string>([clickedChild.key]);

            updatedValues = updatedValues.map((item) => {
              const updatedItem = deepClone(item);

              if (item.children?.length > 0) {
                // Check if any child in this parent matches the target
                const hasMatchingChild = item.children.some((child) =>
                  instanceKeys.has(child.key),
                );

                if (hasMatchingChild) {
                  const parentIsFullyExcluded =
                    item.checked === true && item.isExcluded === true;

                  updatedItem.children = updatedItem.children.map((child) => {
                    if (instanceKeys.has(child.key)) {
                      // Target child: include it
                      return { ...child, checked: true, isExcluded: false };
                    }
                    // Siblings: if parent was fully excluded, explicitly set siblings to excluded
                    // Otherwise, preserve sibling state (when parent is indeterminate)
                    if (parentIsFullyExcluded) {
                      return { ...child, checked: true, isExcluded: true };
                    }
                    // When parent is indeterminate, preserve sibling state
                    return child;
                  });

                  const checkedChildren = updatedItem.children.filter(
                    (c) => c.checked,
                  ).length;
                  const excludedChildren = updatedItem.children.filter(
                    (c) => c.isExcluded,
                  ).length;
                  const includedChildren = updatedItem.children.filter(
                    (c) => c.checked && !c.isExcluded,
                  ).length;

                  // Parent should be indeterminate if children have mixed actions
                  const hasMixedActions =
                    includedChildren > 0 && excludedChildren > 0;

                  if (hasMixedActions) {
                    updatedItem.checked = 'indeterminate';
                    updatedItem.isExcluded = false;
                  } else if (
                    includedChildren === checkedChildren &&
                    checkedChildren === updatedItem.children.length
                  ) {
                    // All children are included - parent should be fully included
                    updatedItem.checked = true;
                    updatedItem.isExcluded = false;
                  } else {
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });
                    // Parent is excluded only if ALL checked children are excluded
                    updatedItem.isExcluded =
                      checkedChildren > 0 &&
                      excludedChildren === checkedChildren;
                  }
                }
              } else if (instanceKeys.has(item.key)) {
                updatedItem.checked = true;
                updatedItem.isExcluded = false;
              }

              return updatedItem;
            });
          }
          break;
        case 'excludeParent':
          if (groupIndex > -1) {
            updatedValues[groupIndex] = {
              ...updatedValues[groupIndex],
              checked: true,
              isExcluded: true,
              children: prevState[groupIndex]?.children?.map((child) => ({
                ...child,
                checked: true,
                isExcluded: true,
              })),
            };
          }
          break;
        case 'excludeChild':
          if (groupIndex > -1 && childIndex > -1) {
            const clickedChild = prevState[groupIndex]?.children[childIndex];

            const instanceKeys = new Set<string>([clickedChild.key]);

            updatedValues = updatedValues.map((item) => {
              const updatedItem = deepClone(item);

              if (item.children?.length > 0) {
                // Check if any child in this parent matches the target
                const hasMatchingChild = item.children.some((child) =>
                  instanceKeys.has(child.key),
                );

                if (hasMatchingChild) {
                  const parentIsFullyIncluded =
                    item.checked === true && item.isExcluded === false;
                  const parentIsFullyExcluded =
                    item.checked === true && item.isExcluded === true;

                  updatedItem.children = updatedItem.children.map((child) => {
                    if (instanceKeys.has(child.key)) {
                      // Target child: exclude it
                      return { ...child, checked: true, isExcluded: true };
                    }
                    // Siblings: if parent was fully excluded, keep siblings excluded
                    if (parentIsFullyExcluded) {
                      return { ...child, checked: true, isExcluded: true };
                    }
                    // Siblings: if parent was fully included, explicitly set siblings to included
                    // Otherwise, preserve sibling state (when parent is indeterminate)
                    if (parentIsFullyIncluded) {
                      return { ...child, checked: true, isExcluded: false };
                    }
                    // When parent is indeterminate, preserve sibling state
                    return child;
                  });

                  const checkedChildren = updatedItem.children.filter(
                    (c) => c.checked,
                  ).length;
                  const excludedChildren = updatedItem.children.filter(
                    (c) => c.isExcluded,
                  ).length;
                  const includedChildren = updatedItem.children.filter(
                    (c) => c.checked && !c.isExcluded,
                  ).length;

                  // Parent should be indeterminate if children have mixed actions
                  const hasMixedActions =
                    includedChildren > 0 && excludedChildren > 0;

                  if (hasMixedActions) {
                    updatedItem.checked = 'indeterminate';
                    updatedItem.isExcluded = false;
                  } else if (
                    excludedChildren === checkedChildren &&
                    checkedChildren === updatedItem.children.length
                  ) {
                    // All children are excluded - parent should be fully excluded
                    updatedItem.checked = true;
                    updatedItem.isExcluded = true;
                  } else {
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });
                    // Parent is excluded only if ALL checked children are excluded
                    updatedItem.isExcluded =
                      checkedChildren > 0 &&
                      excludedChildren === checkedChildren;
                  }
                }
              } else if (instanceKeys.has(item.key)) {
                updatedItem.checked = true;
                updatedItem.isExcluded = true;
              }

              return updatedItem;
            });
          }
          break;
        case 'removeTag': {
          // Company IDs are param values, not param keys — handle removal directly.
          if (filterKey === 'company_ids' && groupName) {
            const id = String(groupName).replace(/"/g, '');
            const removeParams = getParamsToRead();
            const nextIncludeIds = exclude
              ? removeParams
                  .getAll(filterKey)
                  .map((v) => v.replace(/"/g, ''))
                  .filter(Boolean)
              : removeParams
                  .getAll(filterKey)
                  .map((v) => v.replace(/"/g, ''))
                  .filter((v) => v !== id);
            const nextExcludeIds = exclude
              ? removeParams
                  .getAll(excludeKey)
                  .map((v) => v.replace(/"/g, ''))
                  .filter((v) => v !== id)
              : removeParams
                  .getAll(excludeKey)
                  .map((v) => v.replace(/"/g, ''))
                  .filter(Boolean);

            handleQuery({
              filterKey,
              filterExcludeKey: excludeKey,
              filterValue: {
                includedKeys: [...nextIncludeIds, ...companyNameIncludeValues],
                excludedKeys: [...nextExcludeIds, ...companyNameExcludeValues],
              },
              filterType: FilterComponentType.MULTILEVEL_CHECKBOX,
              showExactMatch: exactMatch,
              showIncludePastJobs: shouldSearchPastValues,
              onKeyEnter: !!applyPendingFilters,
            });
            return prevState;
          }

          const removeParams = getParamsToRead();
          const tagKey = removeParams
            .getAll(groupName)
            ?.map((item) => item?.replace(/"/g, '').substring(0, 2));

          if (tagKey?.length > 0) {
            tagKey.forEach((tag) => {
              const tagIndex = checkboxes.findIndex(
                (item) => item?.key?.substring(0, 2) === tag,
              );

              if (tagIndex > -1) {
                if (updatedValues[tagIndex].checked !== 'indeterminate') {
                  updatedValues[tagIndex] = {
                    ...updatedValues[tagIndex],
                    checked: false,
                    children: prevState[tagIndex]?.children?.map((child) => ({
                      ...child,
                      checked: false,
                    })),
                  };
                } else {
                  updatedValues[tagIndex] = {
                    ...updatedValues[tagIndex],
                    children: prevState[tagIndex]?.children?.map((child) =>
                      child.isExcluded === exclude
                        ? { ...child, checked: false }
                        : child,
                    ),
                  };
                }
              }
            });
          } else {
            const tagValue = groupName.replace(/"/g, '');
            const byStorageKey = findInstancesByChildStorageKey(
              tagValue,
              hierarchicalList,
            );
            const allInstances =
              byStorageKey.length > 0
                ? byStorageKey
                : findAllInstancesByValue(tagValue, hierarchicalList);

            if (allInstances.length > 0) {
              const instanceKeys = new Set<string>();
              allInstances.forEach((instance) => {
                instanceKeys.add(instance.childKey);
              });

              updatedValues = updatedValues.map((item) => {
                const updatedItem = deepClone(item);

                if (instanceKeys.has(item.key)) {
                  updatedItem.checked = false;
                  updatedItem.isExcluded = false;
                  updatedItem.children = updatedItem.children.map((child) => ({
                    ...child,
                    checked: false,
                    isExcluded: false,
                  }));
                } else if (item.children?.length > 0) {
                  let hasUpdatedChild = false;
                  updatedItem.children = updatedItem.children.map((child) => {
                    if (instanceKeys.has(child.key)) {
                      hasUpdatedChild = true;
                      return { ...child, checked: false, isExcluded: false };
                    }
                    return child;
                  });

                  if (hasUpdatedChild) {
                    const checkedChildren = updatedItem.children.filter(
                      (c) => c.checked,
                    ).length;
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });
                    updatedItem.isExcluded = false;
                  }
                }

                return updatedItem;
              });
            } else {
              const foundTagKey =
                getFilterKeys([groupName], hierarchicalList)[0] || groupName;
              let localGroupIndex = foundTagKey.replace(/"/g, '')
                ? checkboxes?.findIndex(
                    (c) => c?.key === foundTagKey?.replace(/"/g, ''),
                  )
                : -1;

              if (localGroupIndex > -1) {
                updatedValues[localGroupIndex] = {
                  ...updatedValues[localGroupIndex],
                  checked: false,
                  children: prevState[localGroupIndex]?.children?.map(
                    (child) => ({
                      ...child,
                      checked: false,
                    }),
                  ),
                };
              } else {
                const customTagKey =
                  getFilterKeys([groupName], hierarchicalList)[0] || groupName;
                const foundParentKey = findParentKey(
                  hierarchicalList,
                  customTagKey,
                );
                if (foundParentKey) {
                  localGroupIndex = checkboxes?.findIndex(
                    (c) => c?.key === foundParentKey,
                  );
                }
                if (
                  customTagKey &&
                  localGroupIndex > -1 &&
                  checkboxes[localGroupIndex]?.children?.length > 0
                ) {
                  const localChildIndex = checkboxes[
                    localGroupIndex
                  ].children.findIndex((c) => c?.key === customTagKey);
                  updatedValues[localGroupIndex] = {
                    ...updatedValues[localGroupIndex],
                    children: prevState[
                      localGroupIndex
                    ]?.children?.map((child, cIndex) =>
                      cIndex === localChildIndex
                        ? { ...child, checked: false, isExcluded: false }
                        : child,
                    ),
                  };
                }
              }
            }
          }
          break;
        }
        case 'inputOption':
          includeExcludeOptions(false);
          break;
        case 'excludeOption':
          includeExcludeOptions(true);
          break;
        default:
          break;
      }

      const filteredUpdatedValues = [];
      const uniqueKeys = new Set();

      updatedValues.forEach((item) => {
        if (!uniqueKeys.has(item.key)) {
          uniqueKeys.add(item.key);
          filteredUpdatedValues.push(item);
        }
      });

      const allDeSelected = filteredUpdatedValues.every(
        (item) => !item.checked,
      );

      if (allDeSelected) {
        setExactMatch(false);
        setShouldSearchPastValues(false);
      }

      const latestExactMatch = allDeSelected ? false : exactMatch;
      const latestShouldSearchPastValues = allDeSelected
        ? false
        : shouldSearchPastValues;

      if (isLocationFilter) {
        dispatchLocationQuery(
          filteredUpdatedValues,
          latestExactMatch,
          effectiveIsSearchLeads,
          effectiveOnKeyEnter,
        );
      } else {
        handlePreQuery(
          filteredUpdatedValues,
          latestExactMatch,
          latestShouldSearchPastValues,
          effectiveIsSearchLeads,
          effectiveOnKeyEnter,
          competitorCompanyId,
        );
      }

      return filteredUpdatedValues;
    });
  };

  const debouncedInputChangeRef = useRef<ReturnType<typeof debounce>>();

  const handleInputChange = async (search: string, page = 1) => {
    try {
      setInputError('');

      if (search.trim() === '') {
        setIsLoading(false);
        setShowAutoComplete(false);
        setSelectedSuggestionIndex(-1);
        setSuggestions([]);
        setCurrentPage(1);
        setHasMore(false);
        return;
      }

      const companyKeys = ['company_ids'];
      if (companyKeys.includes(filterKey) && String(search).trim().length < 3) {
        setIsLoading(false);
        setShowAutoComplete(false);
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
        return;
      }

      // Track search term for stale response detection
      searchTermRef.current = search;

      // Company domain is a free-text filter with no suggestions.
      // Don't open an empty autocomplete dropdown.
      if (filterKey === 'company_domain') {
        setIsLoading(false);
        setShowAutoComplete(false);
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
        setHasMore(false);
        return;
      }

      setIsLoading(true);
      setShowAutoComplete(true);

      // Helper to process paginated response
      const handlePaginatedResponse = (payload: any, isV2 = false) => {
        // Guard against stale responses
        if (searchTermRef.current !== search) return;

        setIsLoading(false);
        if (!payload) {
          if (page === 1) {
            const fallbackItems = getSuggestionsWithInputValue([], search);
            if (fallbackItems.length > 0) {
              setSuggestions(fallbackItems);
              setSelectedSuggestionIndex(0);
              setHasMore(false);
            } else {
              setSuggestions([]);
              setSelectedSuggestionIndex(-1);
              setShowAutoComplete(false);
            }
          }
          return;
        }

        // Handle new paginated format { data, hasMore }
        const isPaginated =
          payload?.data !== undefined && payload?.hasMore !== undefined;
        const items = isPaginated ? payload.data : payload;
        const normalizedItems = Array.isArray(items) ? items : [];
        const nextItems =
          page === 1
            ? getSuggestionsWithInputValue(normalizedItems, search)
            : normalizedItems;
        const moreAvailable = isPaginated ? payload.hasMore : false;

        // After include/exclude the input is cleared while the list stays open; gate on `search`, not the DOM value.
        if (String(search || '').trim() === '') return;

        if (page === 1) {
          setSuggestions(nextItems);
        } else {
          setSuggestions((prev) => [...prev, ...nextItems]);
        }
        setHasMore(moreAvailable);
        setIsV2ResponseType(isV2);
        setSelectedSuggestionIndex((currentIndex) => {
          if (
            !shouldSelectFirstSuggestionOnEnter ||
            page !== 1 ||
            nextItems.length === 0
          ) {
            return -1;
          }

          return currentIndex >= 0 && currentIndex < nextItems.length
            ? currentIndex
            : 0;
        });
      };

      if (filterKey === 'company_ids') {
        const res: any = await getCompanyNameDomainAPI({
          searchText: search,
          page,
        });
        handlePaginatedResponse(res?.payload, false);
      } else if (filterKey === 'company_domain') {
        // setShowAutoComplete(false);
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
      } else if (filterKey === 'competitors_company_name') {
        const res: any = await getCompanyNameDomainAPI({
          searchText: search,
          page,
        });
        handlePaginatedResponse(res?.payload, false);
      } else if (filterKey === 'website_domain') {
        const res: any = await getCompanyNameDomainAPI({
          searchText: search,
          page,
        });
        setIsLoading(false);
        if (searchTermRef.current !== search) return;
        if (res?.payload && String(search || '').trim() !== '') {
          const isPaginated = res.payload?.data !== undefined;
          const items = isPaginated ? res.payload.data : res.payload;
          const moreAvailable = isPaginated ? res.payload.hasMore : false;
          const filtered = Array.isArray(items)
            ? items.filter(
                (s: any) => isCompanySuggestion(s) && s.companyDomain,
              )
            : items;
          const finalItems = filtered.length > 0 ? filtered : items;
          if (page === 1) {
            setSuggestions(finalItems);
          } else {
            setSuggestions((prev) => [...prev, ...finalItems]);
          }
          setHasMore(moreAvailable);
          setIsV2ResponseType(false);
          setSelectedSuggestionIndex(-1);
        } else {
          setShowAutoComplete(false);
        }
      } else if (filterKey in V2_MAPPED_KEYS) {
        const apiPayload = {
          type: V2_MAPPED_KEYS[filterKey],
          search,
          paramType:
            filterKey === 'visits_breakdown_by_country' ? 'country' : undefined,
          page,
        };
        const res: GetV2FilterSuggestionResponse = await getV2FilterSuggestionAPI(
          apiPayload,
        );
        handlePaginatedResponse(res?.payload, true);
      } else if (filterKey in MAPPED_KEYS) {
        const res: any = await getFilterSuggestionAPI({
          type: MAPPED_KEYS[filterKey],
          search,
          page,
        });
        handlePaginatedResponse(res?.payload, false);
      } else if (filterKey in LOCAL_SEARCH_MAPPED_KEYS) {
        const dataObject = LOCAL_SEARCH_MAPPED_KEYS[filterKey];
        const localResults = performLocalSearch(search, dataObject, 25, page);
        setIsLoading(false);
        if (searchTermRef.current !== search) return;

        const isPaginated =
          localResults &&
          typeof localResults === 'object' &&
          'data' in localResults;
        const items = isPaginated ? (localResults as any).data : localResults;
        const normalizedItems = Array.isArray(items) ? items : [];
        const nextItems =
          page === 1
            ? getSuggestionsWithInputValue(normalizedItems, search)
            : normalizedItems;
        const moreAvailable = isPaginated
          ? (localResults as any).hasMore
          : false;

        if (nextItems.length > 0 && String(search || '').trim() !== '') {
          if (page === 1) {
            setSuggestions(nextItems);
          } else {
            setSuggestions((prev) => [...prev, ...nextItems]);
          }
          setHasMore(moreAvailable);
          setIsV2ResponseType(false);
          setSelectedSuggestionIndex((currentIndex) => {
            if (
              !shouldSelectFirstSuggestionOnEnter ||
              page !== 1 ||
              nextItems.length === 0
            ) {
              return -1;
            }

            return currentIndex >= 0 && currentIndex < nextItems.length
              ? currentIndex
              : 0;
          });
        } else {
          if (page === 1) {
            setSelectedSuggestionIndex(-1);
            setSuggestions([]);
          }
          setHasMore(false);
        }
      } else {
        setIsLoading(false);
        setShowAutoComplete(false);
      }
    } catch (e) {
      setIsLoading(false);
      setShowAutoComplete(false);
    }
  };

  const fetchNextPage = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    handleInputChange(searchTermRef.current || inputValue, nextPage);
  };

  useEffect(() => {
    debouncedInputChangeRef.current = debounce(handleInputChange, 500);

    return () => {
      debouncedInputChangeRef.current?.cancel();
    };
  }, [filterKey]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const direction = e.key === 'ArrowDown' ? 1 : -1;
      const newIndex = selectedSuggestionIndex + direction;

      if (newIndex >= 0 && newIndex < suggestions?.length) {
        setSelectedSuggestionIndex(newIndex);
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();

      const hasSuggestionSelected =
        selectedSuggestionIndex !== -1 ||
        (suggestions?.length > 0 && shouldSelectFirstSuggestionOnEnter);

      if (hasSuggestionSelected) {
        if (isDropdownIncludeDisabled) return;
        debouncedInputChangeRef.current.cancel();
        const selectedSuggestion =
          suggestions?.[selectedSuggestionIndex] || suggestions?.[0];

        if (handleV2LocationPick(selectedSuggestion, false)) {
          setInputValue('');
          return;
        }

        const suggestionValue = getSuggestionValue(selectedSuggestion);

        if (
          shouldUseInputAsFirstSuggestion &&
          isLocationFilter &&
          typeof selectedSuggestion === 'string' &&
          handleTypedLocationValue(suggestionValue, false)
        ) {
          setInputValue('');
          return;
        }

        // For company_ids, use companyId as groupName and update map
        // For company_domain / website_domain, use companyDomain; skip if empty
        // For competitors_company_name, use companyName
        let groupNameToUse = suggestionValue;
        if (
          filterKey === 'company_ids' &&
          isCompanySuggestion(selectedSuggestion)
        ) {
          groupNameToUse = String(selectedSuggestion.companyId);
          setCompanyIdNameMap?.((prev) => ({
            ...prev,
            [groupNameToUse]: selectedSuggestion.companyName,
          }));
        } else if (
          filterKey === 'competitors_company_name' &&
          isCompanySuggestion(selectedSuggestion)
        ) {
          groupNameToUse = selectedSuggestion.companyName;
        } else if (
          (filterKey === 'company_domain' || filterKey === 'website_domain') &&
          isCompanySuggestion(selectedSuggestion) &&
          selectedSuggestion.companyDomain
        ) {
          groupNameToUse = selectedSuggestion.companyDomain;
        } else if (
          (filterKey === 'company_domain' || filterKey === 'website_domain') &&
          isCompanySuggestion(selectedSuggestion) &&
          !selectedSuggestion.companyDomain
        ) {
          setSuggestions([]);
          setSelectedSuggestionIndex(-1);
          return;
        }

        setInputValue('');
        searchTermRef.current = '';
        setSuggestions([]);
        setShowAutoComplete(false);
        setOnFirstTimeKeyEnter(true);
        handleOnChange({
          action: 'inputOption',
          groupName: groupNameToUse,
          isSearchLeads: false,
          onKeyEnter: !!applyPendingFilters,
          competitorCompanyId:
            filterKey === 'competitors_company_name' &&
            isCompanySuggestion(selectedSuggestion)
              ? selectedSuggestion.companyId
              : undefined,
        });
        setSelectedSuggestionIndex(-1);
        return;
      }

      const trimmedValue = (e.target as HTMLInputElement)?.value.trim();
      if (trimmedValue === '') {
        if (applyPendingFilters) {
          applyPendingFilters();
        } else if (onFirstTimeKeyEnter) {
          setOnFirstTimeKeyEnter(false);
          handleOnChange({
            action: 'inputOption',
            groupName: trimmedValue,
            isSearchLeads: true,
            onKeyEnter: true,
          });
        }
        return;
      }

      if (shouldPreventCustomInput) return;

      debouncedInputChangeRef.current.cancel();
      setOnFirstTimeKeyEnter(true);

      // For Location / Company HQ Location: if the typed value exactly matches
      // a known Region / Country from LOCATION_DATA, route it through the v2
      // key family instead of the legacy string key.
      if (isLocationFilter && handleTypedLocationValue(trimmedValue, false)) {
        setInputValue('');
        return;
      }

      if (blockCompanyNameNumericAtLimit(trimmedValue)) {
        return;
      }

      handleOnChange({
        action: 'inputOption',
        groupName: trimmedValue,
        isSearchLeads: false,
        onKeyEnter: !!applyPendingFilters,
      });
      setInputValue('');
      searchTermRef.current = '';
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
      setShowAutoComplete(false);
    }
  };

  const handleShowExactMatch = (checked: boolean) => {
    if (isDisabledByUnknown) return;
    setExactMatch(checked);
    if (isLocationFilter) {
      dispatchLocationQuery(checkboxes, checked, false, false);
      return;
    }
    handlePreQuery(checkboxes, checked, shouldSearchPastValues, false, false);
  };

  const handleShowIncludePastJobs = (checked: boolean) => {
    if (isDisabledByUnknown) return;
    setShouldSearchPastValues(checked);
    handlePreQuery(checkboxes, exactMatch, checked, false, false);
  };

  const onSelectOption = (option: string) => {
    if (isDropdownIncludeDisabled) return;

    let competitorCompanyId: number | undefined;
    const isTypedInputSuggestion =
      shouldUseInputAsFirstSuggestion &&
      option.trim().toLowerCase() === inputValue.trim().toLowerCase();

    if (
      shouldUseInputAsFirstSuggestion &&
      isLocationFilter &&
      handleTypedLocationValue(option, false)
    ) {
      setInputValue('');
      setSelectedSuggestionIndex(-1);
      return;
    }

    // For company_ids, update the map with ID -> name before proceeding
    if (filterKey === 'company_ids') {
      const companySuggestion = suggestions?.find(
        (s) => isCompanySuggestion(s) && String(s.companyId) === option,
      );
      if (companySuggestion && isCompanySuggestion(companySuggestion)) {
        setCompanyIdNameMap?.((prev) => ({
          ...prev,
          [option]: companySuggestion.companyName,
        }));
      }
    }

    // For competitors_company_name, get companyId from suggestion
    if (filterKey === 'competitors_company_name') {
      const companySuggestion = suggestions?.find(
        (s) => isCompanySuggestion(s) && s.companyName === option,
      );
      if (companySuggestion && isCompanySuggestion(companySuggestion)) {
        competitorCompanyId = companySuggestion.companyId;
      }
    }

    handleOnChange({
      action: 'inputOption',
      groupName: option,
      isSearchLeads: false,
      competitorCompanyId,
    });
    if (hideIncludeExclude || isTypedInputSuggestion) {
      setInputValue('');
      searchTermRef.current = '';
      setSuggestions([]);
      setShowAutoComplete(false);
    }
    setSelectedSuggestionIndex(-1);
  };

  const handleToggleExpand = (key: string) => {
    if (expanded.includes(key)) {
      setExpanded((d) => d.filter((i) => i !== key));
    } else {
      setExpanded((d) => [...d, key]);
    }
  };

  const handleOnChangeRef = useRef(handleOnChange);
  handleOnChangeRef.current = handleOnChange;
  const stableHandleOnChange = useCallback(
    (params: HandleOnChangeParams) => handleOnChangeRef.current(params),
    [],
  );

  const handleToggleExpandRef = useRef(handleToggleExpand);
  handleToggleExpandRef.current = handleToggleExpand;
  const stableHandleToggleExpand = useCallback(
    (key: string) => handleToggleExpandRef.current(key),
    [],
  );

  const onLockedClickRef = useRef(onLockedClick);
  onLockedClickRef.current = onLockedClick;
  const stableOnLockedClick = useCallback(() => onLockedClickRef.current?.(), []);


  const initialCheckboxKeys = useMemo(
    () => new Set(initialCheckboxes.map((initial) => initial?.key)),
    [initialCheckboxes],
  );
  const visibleCheckboxes = useMemo(
    () => checkboxes?.filter((item) => initialCheckboxKeys.has(item?.key)) ?? [],
    [checkboxes, initialCheckboxKeys],
  );

  // Sync checkboxes with pendingFilters or URL params
  // This effect runs when location.search changes, and also when component re-renders
  // (which happens when pendingFilters changes in the parent)
  useEffect(() => {
    const currentParams = getParamsToRead();
    const legacyIncludedItems = currentParams
      .getAll(filterKey)
      .map((item) => item.replace(/"/g, ''));
    const legacyExcludedItems = currentParams
      .getAll(excludeKey)
      .map((item) => item.replace(/"/g, ''));

    // Union v2 decoded entries so Region:/Country: params drive checkbox state.
    const v2LocationKeys = locationV2Keys;
    const v2IncludeDecoded = v2LocationKeys
      ? currentParams
          .getAll(v2LocationKeys.v2Include)
          .map(decodeLocationV2Entry)
          .filter(Boolean)
          .map((e) => (e as { value: string }).value)
      : [];
    const v2ExcludeDecoded = v2LocationKeys
      ? currentParams
          .getAll(v2LocationKeys.v2Exclude)
          .map(decodeLocationV2Entry)
          .filter(Boolean)
          .map((e) => (e as { value: string }).value)
      : [];

    const includedItemsFromParams = Array.from(
      new Set([...legacyIncludedItems, ...v2IncludeDecoded]),
    );
    const excludedItemsFromParams = Array.from(
      new Set([...legacyExcludedItems, ...v2ExcludeDecoded]),
    );

    if (
      includedItemsFromParams.length === 0 &&
      excludedItemsFromParams.length === 0
    ) {
      setExpanded([]);
    }

    setCheckboxes((prevState) => {
      let newState = [];
      if (prevState?.length > 0) {
        newState = prevState.map((item) => {
          let children = [];
          if (item?.children?.length > 0) {
            children = item?.children?.map((child) => ({
              ...child,
              isExcluded:
                excludedItemsFromParams.includes(child?.key) ||
                excludedItemsFromParams.includes(child?.value ?? ''),
              checked:
                includedItemsFromParams.includes(item?.key) ||
                excludedItemsFromParams.includes(item?.key) ||
                includedItemsFromParams.includes(child?.key) ||
                excludedItemsFromParams.includes(child?.key) ||
                includedItemsFromParams.includes(child?.value ?? '') ||
                excludedItemsFromParams.includes(child?.value ?? ''),
            }));
          }
          const checkedChild = children.map((c) => c?.checked).filter(Boolean)
            .length;
          let parentChecked = calculateCheckState({
            checkedChild,
            totalChild: children.length,
          });

          // Check if parent key is explicitly in URL params
          const parentInParams =
            includedItemsFromParams.includes(item?.key) ||
            excludedItemsFromParams.includes(item?.key);

          if (parentInParams) {
            parentChecked = true;
          }

          // Set parent's isExcluded based on URL params
          const parentIsExcluded = excludedItemsFromParams.includes(item?.key);

          // If parent key is in URL params, use that state
          // Otherwise, calculate from children
          if (parentInParams) {
            return {
              ...item,
              checked: parentChecked,
              isExcluded: parentIsExcluded,
              children,
            };
          }

          // Parent not in URL params - calculate from children
          const includedChildren = children.filter(
            (c) => c.checked && !c.isExcluded,
          ).length;
          const excludedChildren = children.filter((c) => c.isExcluded).length;
          const hasMixedActions = includedChildren > 0 && excludedChildren > 0;

          if (hasMixedActions) {
            // Parent is indeterminate - isExcluded should be false
            return {
              ...item,
              checked: 'indeterminate',
              isExcluded: false,
              children,
            };
          }

          // All children in same state
          return {
            ...item,
            checked: parentChecked,
            isExcluded: excludedChildren === checkedChild && checkedChild > 0,
            children,
          };
        });
      }
      return newState;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.search, filterKey, excludeKey, pendingFilters]);

  useEffect(() => {
    if (isExpand) {
      setInputValue('');
      setSuggestions([]);
      setShowAutoComplete(false);
    } else {
      setShowAllIncludeTags(false);
      setShowAllExcludeTags(false);
    }
  }, [isExpand]);

  useEffect(() => {
    if (inputRef?.current && isExpand) {
      if (
        ['role_and_department', 'company_industry', 'education'].includes(
          parentKey,
        )
      ) {
        if (['job_title', 'company_industry', 'major'].includes(filterKey)) {
          inputRef?.current.focus();
        }
      } else {
        inputRef?.current.focus();
      }
    }
  }, [isExpand, parentKey, filterKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || hideSearchBox) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const targetDiv = document.getElementById(suggestionsContainerID);
      if (!targetDiv || !event) return;

      // Robust "inside" detection: React / portals / SVG clicks can make
      // `event.target` checks flaky. Prefer composedPath when available.
      const path = (event as any).composedPath?.() as EventTarget[] | undefined;
      const clickedInsideContainer =
        (path && path.includes(targetDiv)) ||
        (!!event.target && targetDiv.contains(event.target as Node));

      const clickedInsideAutocomplete =
        !!path &&
        path.some((t) => {
          const el = t as HTMLElement;
          if (!el?.classList) return false;
          return (
            el.classList.contains('autocomplete') ||
            (typeof el.id === 'string' &&
              el.id.startsWith('autocomplete-scroll-'))
          );
        });

      const clickedInside = clickedInsideContainer || clickedInsideAutocomplete;

      if (!clickedInside) {
        debouncedInputChangeRef.current?.cancel();
        if (showAutoComplete) {
          setInputValue('');
          searchTermRef.current = '';
          setSuggestions([]);
          setSelectedSuggestionIndex(-1);
          setInputError('');
        }
        setShowAutoComplete(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [hideSearchBox, suggestionsContainerID, showAutoComplete]);

  // for handling show exact match in referesh or location changes in url
  useEffect(() => {
    const currentParams = getParamsToRead();
    const exactMatchParam = currentParams.get(`${filterKey}_exactMatch`);
    // For Location / Company HQ Location, also honor the v2 flag variant.
    const v2ExactMatchParam = locationV2Keys
      ? currentParams.get(`${locationV2Keys.v2Include}_exactMatch`)
      : null;
    if (exactMatchParam === '1' || v2ExactMatchParam === '1') {
      setExactMatch(true);
    } else {
      setExactMatch(false);
    }
    const shouldSearchPastParam =
      currentParams.get(`${filterKey}_shouldSearchPast`) ||
      (filterKey === 'company_ids'
        ? currentParams.get('company_name_shouldSearchPast')
        : null);
    if (shouldSearchPastParam === '1') {
      setShouldSearchPastValues(true);
    } else {
      setShouldSearchPastValues(false);
    }
  }, [location?.search, filterKey, pendingFilters]);

  // Update checkbox values when companyIdNameMap changes (for company_ids)
  useEffect(() => {
    if (
      filterKey !== 'company_ids' ||
      Object.keys(effectiveCompanyIdNameMap).length === 0
    ) {
      return;
    }

    setCheckboxes((prevCheckboxes) =>
      prevCheckboxes.map((checkbox) => ({
        ...checkbox,
        value: effectiveCompanyIdNameMap[checkbox.key] || checkbox.value,
      })),
    );
  }, [effectiveCompanyIdNameMap, filterKey]);

  const filterValues = getFilterValuesWithExtraValues(
    paramsToRead.getAll(filterKey)?.map((item) => item?.replace(/"/g, '')),
    hierarchicalList,
  );
  const excludeValues = getFilterValuesWithExtraValues(
    paramsToRead.getAll(excludeKey)?.map((item) => item?.replace(/"/g, '')),
    hierarchicalList,
  );
  const datePresetOptions = useMemo(
    () =>
      hierarchicalList.map((item) => ({
        key: item.key,
        label: item.value,
      })),
    [hierarchicalList],
  );

  const getSelectedTagDisplayPayload = useCallback(
    (selectedTag: string) => {
      const resolvedValue =
        filterKey === 'company_ids'
          ? effectiveCompanyIdNameMap[selectedTag] || selectedTag
          : selectedTag;
      const presetLabel = getFundingDatePresetLabel(
        selectedTag,
        datePresetOptions,
      );
      const presetByLabel = hierarchicalList.find(
        (item) =>
          item?.value === resolvedValue &&
          typeof item?.key === 'string' &&
          item.key.includes(' - '),
      );
      const hasPresetLabel = !!presetLabel || !!presetByLabel;

      return {
        displayValue: presetLabel || resolvedValue,
        tooltipValue: presetByLabel?.key || resolvedValue,
        hasPresetLabel,
      };
    },
    [filterKey, effectiveCompanyIdNameMap, hierarchicalList, datePresetOptions],
  );

  // Autocomplete "Included/Excluded" chips should reflect the live checkbox state
  // immediately (before URL params are applied / pushed).
  const autocompleteIncludedGroupNames = useMemo(() => {
    const checkedKeys = getCheckedKeys(checkboxes, true, exactMatch);
    return Array.from(
      new Set([
        ...checkedKeys,
        ...getFilterValuesWithExtraValues(checkedKeys, hierarchicalList),
        ...(filterKey === 'company_industry'
          ? expandIndustryParentToChildren(checkedKeys, hierarchicalList)
          : []),
      ]),
    );
  }, [checkboxes, exactMatch, filterKey, hierarchicalList]);
  const autocompleteExcludedGroupNames = useMemo(() => {
    const checkedKeys = getCheckedKeys(checkboxes, false, exactMatch);
    return Array.from(
      new Set([
        ...checkedKeys,
        ...getFilterValuesWithExtraValues(checkedKeys, hierarchicalList),
        ...(filterKey === 'company_industry'
          ? expandIndustryParentToChildren(checkedKeys, hierarchicalList)
          : []),
      ]),
    );
  }, [checkboxes, exactMatch, filterKey, hierarchicalList]);

  const updateCompanyNameQuery = (
    nextIncludes: string[],
    nextExcludes: string[],
  ) => {
    if (filterKey !== 'company_ids') return;
    const currentParams = getParamsToRead();
    const currentExactMatch =
      currentParams.get('company_name_exactMatch') === '1';
    const hasAnyValues = nextIncludes.length > 0 || nextExcludes.length > 0;

    const hasCompanyIdsValues =
      currentParams.getAll('company_ids').filter(Boolean).length > 0 ||
      currentParams.getAll('excludeCompany_ids').filter(Boolean).length > 0;

    const allFiltersRemoved = !hasAnyValues && !hasCompanyIdsValues;

    if (allFiltersRemoved) {
      setShouldSearchPastValues(false);
    }

    handleQuery?.({
      filterKey: 'company_name',
      filterValue: {
        includedKeys: nextIncludes,
        excludedKeys: nextExcludes,
      },
      filterType: FilterComponentType.MULTILEVEL_CHECKBOX,
      filterExcludeKey: 'excludeCompany_name',
      // If there are no remaining values, ensure exactMatch and past jobs flags are cleared.
      showExactMatch: hasAnyValues ? currentExactMatch : false,
      ...(allFiltersRemoved && { showIncludePastJobs: false }),
      onKeyEnter: !!applyPendingFilters,
    });
  };

  const showInputIncludeExclude = !hideIncludeExclude && inputValue;
  const isInputBlank = !inputValue?.trim();

  const clearCompanyDomainInput = () => {
    if (filterKey !== 'company_domain') return;
    setInputValue('');
    searchTermRef.current = '';
  };

  const renderFilterSearchInput = () => (
    <div
      className={`position-relative${
        showInputIncludeExclude && !disableCustomInput
          ? ' with-input-icons'
          : ''
      }`}
    >
      <input
        id={searchInputContainerId}
        ref={inputRef}
        type="text"
        className={classNames('auto-search-input position-relative', {
          'error-input-border': Boolean(inputError),
        })}
        autoComplete="off"
        onChange={(e) => {
          debouncedInputChangeRef.current.cancel();
          const enteredValue = e?.target?.value;
          setCurrentPage(1);
          setHasMore(false);
          if (shouldUseInputAsFirstSuggestion || shouldPreventCustomInput) {
            searchTermRef.current = enteredValue;
            setSuggestions([]);
            setShowAutoComplete(false);
            setSelectedSuggestionIndex(-1);
          }
          debouncedInputChangeRef.current(enteredValue);
          setInputValue(enteredValue);
          if (inputError) setInputError('');
        }}
        onKeyDown={onKeyDown}
        value={inputValue}
        placeholder={inputPlaceHolder ?? 'Type and press Enter'}
        maxLength={200}
      />

      {showInputIncludeExclude && !shouldPreventCustomInput && (
        <div className="include-exclude-icons position-absolute">
          <OverlayTrigger
            placement={Placement.Bottom}
            overlay={
              <Tooltip
                id={`tooltip-${inputValue}-include-child-${inputValue}`}
                className="bs-tooltip-inner"
              >
                {includeTooltip}
              </Tooltip>
            }
          >
            <CirclePlus
              className={`circle-plus ${
                isIncludeDisabled || isInputBlank
                  ? 'include-disabled'
                  : 'include-enabled'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                const trimmedValue = inputValue.trim();
                if (!isIncludeDisabled && !isInputBlank) {
                  if (
                    isLocationFilter &&
                    handleTypedLocationValue(trimmedValue, false)
                  ) {
                    return;
                  }
                  if (blockCompanyNameNumericAtLimit(trimmedValue)) {
                    return;
                  }
                  handleOnChange({
                    action: 'inputOption',
                    groupName: trimmedValue,
                  });
                  clearCompanyDomainInput();
                }
              }}
            />
          </OverlayTrigger>

          <OverlayTrigger
            placement={Placement.Bottom}
            overlay={
              <Tooltip
                id={`tooltip-${inputValue}-exclude-child-${inputValue}`}
                className="bs-tooltip-inner"
              >
                Exclude
              </Tooltip>
            }
          >
            <CircleMinus
              className={`circle-minus ${
                isInputBlank ? 'include-disabled' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                const trimmedValue = inputValue.trim();
                if (isInputBlank) return;
                if (
                  isLocationFilter &&
                  handleTypedLocationValue(trimmedValue, true)
                ) {
                  return;
                }
                if (blockCompanyNameNumericAtLimit(trimmedValue)) {
                  return;
                }
                handleOnChange({
                  action: 'excludeOption',
                  groupName: trimmedValue,
                });
                clearCompanyDomainInput();
              }}
            />
          </OverlayTrigger>
        </div>
      )}
    </div>
  );

  const totalIncludeCount =
    filterValues?.length +
    companyNameIncludeValues?.length +
    v2IncludeRaw?.length;
  const filterValuesSlotsUsed = showAllIncludeTags
    ? filterValues?.length
    : Math.min(filterValues?.length, effectiveExpandedMaxFilterTags);

  const companyNameSlotsRemaining = showAllIncludeTags
    ? companyNameIncludeValues?.length
    : Math.max(0, effectiveExpandedMaxFilterTags - filterValuesSlotsUsed);
  const companyNameDisplayed = showAllIncludeTags
    ? companyNameIncludeValues
    : companyNameIncludeValues?.slice(0, companyNameSlotsRemaining);
  const companyNameSlotsUsed = companyNameDisplayed?.length ?? 0;

  const v2IncludeSlots = showAllIncludeTags
    ? v2IncludeRaw?.length
    : Math.max(
        0,
        effectiveExpandedMaxFilterTags -
          filterValuesSlotsUsed -
          companyNameSlotsUsed,
      );
  const v2IncludeDisplayed = showAllIncludeTags
    ? v2IncludeRaw
    : v2IncludeRaw.slice(0, v2IncludeSlots);

  const totalExcludeCount =
    (excludeValues?.length ?? 0) +
    companyNameExcludeValues?.length +
    v2ExcludeRaw?.length;

  const handleAppliedTagRemove = (removeAction: () => void) => {
    const isLastTagInFilterGroup =
      parentAppliedTagCount != null && parentAppliedTagCount === 1;
    if (
      isLastTagInFilterGroup &&
      isOnlyAppliedFilter &&
      onClearLastAppliedFilter
    ) {
      onClearLastAppliedFilter();
      return;
    }
    removeAction();
  };

  const excludeValuesSlotsUsed = showAllExcludeTags
    ? excludeValues?.length ?? 0
    : Math.min(excludeValues?.length ?? 0, effectiveExpandedMaxFilterTags);

  const companyNameExcludeSlotsRemaining = showAllExcludeTags
    ? companyNameExcludeValues?.length
    : Math.max(0, effectiveExpandedMaxFilterTags - excludeValuesSlotsUsed);
  const companyNameExcludeDisplayed = showAllExcludeTags
    ? companyNameExcludeValues
    : companyNameExcludeValues?.slice(0, companyNameExcludeSlotsRemaining);
  const companyNameExcludeSlotsUsed = companyNameExcludeDisplayed?.length ?? 0;

  const v2ExcludeSlots = showAllExcludeTags
    ? v2ExcludeRaw?.length
    : Math.max(
        0,
        effectiveExpandedMaxFilterTags -
          excludeValuesSlotsUsed -
          companyNameExcludeSlotsUsed,
      );
  const v2ExcludeDisplayed = showAllExcludeTags
    ? v2ExcludeRaw
    : v2ExcludeRaw.slice(0, v2ExcludeSlots);

  return (
    <>
      <div
        className={`input-container ${inputClassName} ${
          isDisabledByUnknown ? 'filter-disabled-by-unknown' : ''
        }`}
        id={suggestionsContainerID}
      >
        {inputLabel && <p className="blue-txt-15 label">{inputLabel}</p>}
        {!hideSearchBox && (
          <>
            {isDisabledByUnknown ? (
              <OverlayTooltip
                placement={Placement.Top}
                text={DISABLED_BY_UNKNOWN_TOOLTIP}
                className="bs-tooltip-inner disabled-by-unknown-tooltip"
              >
                <div className="position-relative disabled-filter-wrapper">
                  <input
                    id={searchInputContainerId}
                    ref={inputRef}
                    type="text"
                    className="auto-search-input position-relative"
                    autoComplete="off"
                    disabled
                    value={inputValue}
                    placeholder={inputPlaceHolder ?? 'Type and press Enter'}
                    maxLength={200}
                  />
                </div>
              </OverlayTooltip>
            ) : isIncludeDisabled &&
              (filterKey === 'competitors_company_name' ||
                filterKey === 'website_domain') ? (
              <OverlayTooltip
                placement={Placement.Top}
                text={includeTooltip}
                className="bs-tooltip-inner"
              >
                <div className="position-relative disabled-filter-wrapper">
                  <input
                    id={searchInputContainerId}
                    ref={inputRef}
                    type="text"
                    className="auto-search-input position-relative error-input-border"
                    autoComplete="off"
                    disabled
                    value={inputValue}
                    placeholder={inputPlaceHolder ?? 'Type and press Enter'}
                    maxLength={200}
                  />
                </div>
              </OverlayTooltip>
            ) : inputError ? (
              <OverlayTooltip
                placement={Placement.Top}
                text={inputError}
                className="bs-tooltip-inner"
              >
                {renderFilterSearchInput()}
              </OverlayTooltip>
            ) : (
              renderFilterSearchInput()
            )}
            {advanceFilters?.showExactMatchCheckbox && (
              <div>
                <Checkbox
                  value={filterKey}
                  className="w-100 show-exact-match-check"
                  onChange={handleShowExactMatch}
                  checked={exactMatch}
                  disabled={
                    isDisabledByUnknown ||
                    (filterValues?.length === 0 &&
                      excludeValues?.length === 0 &&
                      v2IncludeRaw.length === 0 &&
                      v2ExcludeRaw.length === 0)
                  }
                  label={
                    <div className="label-container d-inline-block mt-2">
                      <p className="checkbox-label m-0">Show Exact Match</p>
                    </div>
                  }
                />
              </div>
            )}
            {advanceFilters?.showIncludePastJobsCheckbox && (
              <div>
                <Checkbox
                  value={`${filterKey}_shouldSearchPast`}
                  className="w-100 should-search-past-filter"
                  onChange={handleShowIncludePastJobs}
                  checked={shouldSearchPastValues}
                  disabled={
                    isDisabledByUnknown ||
                    (filterValues?.length === 0 &&
                      excludeValues?.length === 0 &&
                      companyNameIncludeValues?.length === 0 &&
                      companyNameExcludeValues?.length === 0)
                  }
                  label={
                    <div className="label-container d-inline-block mt-2">
                      <p className="checkbox-label m-0">
                        Should Search Past Experiences
                      </p>
                    </div>
                  }
                />
              </div>
            )}

            {showAutoComplete && (
              <RenderAutocomplete
                {...{
                  suggestions,
                  onSelectOption,
                  filterKey,
                  selectedSuggestionIndex,
                  handleOnChange,
                  setShowAutoComplete,
                  isIncludeDisabled: isDropdownIncludeDisabled,
                  isExcludeDisabled: isDropdownExcludeDisabled,
                  includeTooltip: dropdownIncludeTooltip,
                  excludeTooltip: dropdownExcludeTooltip,
                  isV2Response: isV2ResponseType,
                  isLoading,
                  hideIncludeExclude,
                  inputLabel,
                  hasMore,
                  onLoadMore: fetchNextPage,
                  onV2LocationPick: isLocationFilter
                    ? handleV2LocationPick
                    : undefined,
                  onV2LocationReset: isLocationFilter
                    ? handleV2LocationReset
                    : undefined,
                  includedGroupNames: autocompleteIncludedGroupNames,
                  excludedGroupNames: autocompleteExcludedGroupNames,
                }}
              />
            )}
          </>
        )}
      </div>
      <div className="d-flex flex-column">
        {(filterValues?.length > 0 ||
          companyNameIncludeValues.length > 0 ||
          v2IncludeRaw.length > 0) && (
          <>
            {!hideIncludeExclude && (
              <p className="includes-excludes-text mt-2">Includes</p>
            )}
            <div className={`${hideIncludeExclude ? 'mb-2' : ''}`}>
              {(showAllIncludeTags
                ? filterValues
                : filterValues?.slice(0, effectiveExpandedMaxFilterTags)
              )?.map((selectedTag) => {
                const {
                  displayValue,
                  tooltipValue,
                } = getSelectedTagDisplayPayload(selectedTag);

                return (
                  <span
                    key={`tag-${selectedTag}`}
                    className="d-inline-block"
                    onMouseEnter={() => setShowTagTooltip(selectedTag)}
                    onMouseLeave={() => setShowTagTooltip(null)}
                  >
                    <OverlayTooltip
                      placement={Placement.BottomStart}
                      text={tooltipValue}
                    >
                      <div>
                        <Tag
                          value={
                            displayValue?.length > 15
                              ? `${displayValue
                                  .substring(0, 15)
                                  .replace(/"/g, '')}..`
                              : displayValue.replace(/"/g, '')
                          }
                          variant={Tag.Variant.Remove}
                          className="filter-tag"
                          onClick={() =>
                            handleAppliedTagRemove(() =>
                              handleOnChange({
                                action: 'removeTag',
                                groupName: selectedTag,
                              }),
                            )
                          }
                        />
                      </div>
                    </OverlayTooltip>
                  </span>
                );
              })}
              {companyNameDisplayed.map((value) => (
                <span
                  key={`company_name-tag-${value}`}
                  className="d-inline-block"
                  onMouseEnter={() =>
                    setShowTagTooltip(`company_name:${value}`)
                  }
                  onMouseLeave={() => setShowTagTooltip(null)}
                >
                  <OverlayTooltip
                    placement={Placement.BottomStart}
                    text={value}
                  >
                    <div>
                      <Tag
                        value={
                          value?.length > 15
                            ? `${value.substring(0, 15).replace(/"/g, '')}..`
                            : value.replace(/"/g, '')
                        }
                        variant={Tag.Variant.Remove}
                        className="filter-tag"
                        onClick={() => {
                          handleAppliedTagRemove(() => {
                            const nextIncludes = companyNameIncludeValues.filter(
                              (v) => v !== value,
                            );
                            updateCompanyNameQuery(
                              nextIncludes,
                              companyNameExcludeValues,
                            );
                          });
                        }}
                      />
                    </div>
                  </OverlayTooltip>
                </span>
              ))}
              {v2IncludeDisplayed.map((encoded) => {
                const displayValue = getV2LocationTagLabel(encoded);
                if (!displayValue) return null;
                return (
                  <span
                    key={`v2-include-${encoded}`}
                    className="d-inline-block"
                    onMouseEnter={() =>
                      setShowTagTooltip(`v2-include:${encoded}`)
                    }
                    onMouseLeave={() => setShowTagTooltip(null)}
                  >
                    <OverlayTooltip
                      placement={Placement.BottomStart}
                      text={displayValue}
                    >
                      <div>
                        <Tag
                          value={
                            displayValue?.length > 15
                              ? `${displayValue.substring(0, 15)}..`
                              : displayValue
                          }
                          variant={Tag.Variant.Remove}
                          className="filter-tag"
                          onClick={() =>
                            handleAppliedTagRemove(() =>
                              handleV2LocationRemove(encoded, false),
                            )
                          }
                        />
                      </div>
                    </OverlayTooltip>
                  </span>
                );
              })}
              {!showAllIncludeTags &&
              totalIncludeCount > effectiveExpandedMaxFilterTags ? (
                <Tag
                  value={
                    <div className="d-flex align-items-center">
                      <Plus
                        className="gray-txt-15 mr-1"
                        width={13}
                        height={13}
                      />
                      <p className="mb-0">
                        {totalIncludeCount - effectiveExpandedMaxFilterTags}
                      </p>
                    </div>
                  }
                  className="filter-tag more-tag"
                  onClick={() => setShowAllIncludeTags(true)}
                />
              ) : null}
            </div>
          </>
        )}
        {!hideIncludeExclude &&
          (excludeValues?.length > 0 ||
            companyNameExcludeValues.length > 0 ||
            v2ExcludeRaw.length > 0) && (
            <>
              <p
                className={`includes-excludes-text ${
                  removeTopMargin && filterValues?.length === 0 ? 'mt-0' : ''
                }`}
              >
                Excludes
              </p>
              <div>
                {(showAllExcludeTags
                  ? excludeValues
                  : excludeValues?.slice(0, effectiveExpandedMaxFilterTags)
                )?.map((selectedTag) => {
                  const {
                    displayValue,
                    tooltipValue,
                    hasPresetLabel,
                  } = getSelectedTagDisplayPayload(selectedTag);

                  return (
                    <span
                      key={`tag-${selectedTag}`}
                      className="d-inline-block"
                      onMouseEnter={() => setShowTagTooltip(selectedTag)}
                      onMouseLeave={() => setShowTagTooltip(null)}
                    >
                      <OverlayTrigger
                        placement={Placement.BottomStart}
                        overlay={getLabelTooltip(tooltipValue)}
                        show={
                          showTagTooltip === selectedTag &&
                          (hasPresetLabel || displayValue?.length > 15)
                        }
                      >
                        <Tag
                          value={
                            displayValue?.length > 15
                              ? `${displayValue
                                  .substring(0, 15)
                                  .replace(/"/g, '')}..`
                              : displayValue.replace(/"/g, '')
                          }
                          variant={Tag.Variant.Remove}
                          className="filter-tag"
                          onClick={() =>
                            handleAppliedTagRemove(() =>
                              handleOnChange({
                                action: 'removeTag',
                                groupName: selectedTag,
                                exclude: true,
                              }),
                            )
                          }
                        />
                      </OverlayTrigger>
                    </span>
                  );
                })}
                {companyNameExcludeDisplayed.map((value) => (
                  <span
                    key={`excludeCompany_name-tag-${value}`}
                    className="d-inline-block"
                    onMouseEnter={() =>
                      setShowTagTooltip(`excludeCompany_name:${value}`)
                    }
                    onMouseLeave={() => setShowTagTooltip(null)}
                  >
                    <OverlayTrigger
                      placement={Placement.BottomStart}
                      overlay={getLabelTooltip(value)}
                      show={
                        showTagTooltip === `excludeCompany_name:${value}` &&
                        value?.length > 15
                      }
                    >
                      <Tag
                        value={
                          value?.length > 15
                            ? `${value.substring(0, 15).replace(/"/g, '')}..`
                            : value.replace(/"/g, '')
                        }
                        variant={Tag.Variant.Remove}
                        className="filter-tag"
                        onClick={() => {
                          handleAppliedTagRemove(() => {
                            const nextExcludes = companyNameExcludeValues.filter(
                              (v) => v !== value,
                            );
                            updateCompanyNameQuery(
                              companyNameIncludeValues,
                              nextExcludes,
                            );
                          });
                        }}
                      />
                    </OverlayTrigger>
                  </span>
                ))}
                {v2ExcludeDisplayed.map((encoded) => {
                  const displayValue = getV2LocationTagLabel(encoded);
                  if (!displayValue) return null;
                  return (
                    <span
                      key={`v2-exclude-${encoded}`}
                      className="d-inline-block"
                      onMouseEnter={() =>
                        setShowTagTooltip(`v2-exclude:${encoded}`)
                      }
                      onMouseLeave={() => setShowTagTooltip(null)}
                    >
                      <OverlayTrigger
                        placement={Placement.BottomStart}
                        overlay={getLabelTooltip(displayValue)}
                        show={
                          showTagTooltip === `v2-exclude:${encoded}` &&
                          displayValue?.length > 15
                        }
                      >
                        <Tag
                          value={
                            displayValue?.length > 15
                              ? `${displayValue.substring(0, 15)}..`
                              : displayValue
                          }
                          variant={Tag.Variant.Remove}
                          className="filter-tag"
                          onClick={() =>
                            handleAppliedTagRemove(() =>
                              handleV2LocationRemove(encoded, true),
                            )
                          }
                        />
                      </OverlayTrigger>
                    </span>
                  );
                })}
                {!showAllExcludeTags &&
                totalExcludeCount > effectiveExpandedMaxFilterTags ? (
                  <Tag
                    value={
                      <div className="d-flex align-items-center">
                        <Plus
                          className="gray-txt-15 mr-1"
                          width={13}
                          height={13}
                        />
                        <p className="mb-0">
                          {totalExcludeCount - effectiveExpandedMaxFilterTags}
                        </p>
                      </div>
                    }
                    className="filter-tag more-tag"
                    onClick={() => setShowAllExcludeTags(true)}
                  />
                ) : null}
              </div>
            </>
          )}
        {visibleCheckboxes.length > 0 && (
          <div
            className={classNames('v2-filter-checkbox-list', {
              'mt-0':
                removeTopMargin &&
                filterValues?.length === 0 &&
                excludeValues?.length === 0,
            })}
          >
            {visibleCheckboxes.map((item) => (
              <RenderCheckboxGroup
                key={`item-${item.key}`}
                name={item.key}
                label={item?.value}
                checked={item.checked as boolean | 'indeterminate'}
                childItems={item.children}
                expanded={expanded}
                handleOnChange={stableHandleOnChange}
                filterKey={filterKey}
                infoCircleToolTip={infoCircleToolTipMapping?.[item?.key]}
                hideIncludeExclude={hideIncludeExclude}
                isIncludeDisabled={isIncludeDisabled}
                includeTooltip={includeTooltip}
                isPhoneNumberNudgeOpen={isPhoneNumberNudgeOpen}
                onToggleExpand={stableHandleToggleExpand}
                tooltipText={(item as CheckboxItem)?.tooltipText}
                parentLocked={(item as CheckboxItem)?.isLocked}
                onLockClick={stableOnLockedClick}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default V2FilterCheckbox;
